import type { IDataInstance, IAtom, IType, IRelation, ITuple, IInputDataInstance } from './interfaces';
import { DataInstanceEventEmitter } from './data-instance-event-emitter';
import { settleTupleTypes } from './tuple-types';
import type { AlloyType, AlloyAtom, AlloyRelation, AlloyTuple } from './alloy/alloy-instance';
import { addInstanceAtom, addInstanceRelationTuple, removeInstanceRelationTuple, AlloyInstance, removeInstanceAtom } from './alloy/alloy-instance';
import {
  getInstanceAtoms,
  getInstanceTypes,
  getInstanceRelations,
  getSkolemNamesForAtom
} from './alloy/alloy-instance';
import { getAtomType } from './alloy/alloy-instance/src/atom';
import { isBuiltin } from './alloy/alloy-instance/src/type';
import { generateGraph } from './alloy/alloy-graph';
import { Graph } from 'graphlib';

/**
 * Implementation of IDataInstance for Alloy instances
 * Wraps the existing AlloyInstance to provide the IDataInstance interface
 */
export class AlloyDataInstance extends DataInstanceEventEmitter implements IInputDataInstance {
  private alloyInstance: AlloyInstance;
  /**
   * Sig ids / relation names present in the ORIGINAL parsed instance, snapshotted
   * at construction. `reify()` only binds these. Editing (`addAtom` /
   * `addRelationTuple`) can fabricate sigs and fields the spec never declared, and
   * binding those produces an `inst` that fails to compile on paste-back (issue
   * #480). These sets are intentionally NOT updated on edit — so an
   * `AlloyDataInstance` must be constructed from a pristine parsed instance for the
   * snapshot to mean "what the spec declares."
   */
  private readonly declaredTypeIds: Set<string>;
  private readonly declaredRelationNames: Set<string>;

  constructor(alloyInstance: AlloyInstance) {
    super();
    this.alloyInstance = alloyInstance;
    this.declaredTypeIds = new Set(Object.keys(alloyInstance.types));
    this.declaredRelationNames = new Set(Object.values(alloyInstance.relations).map((relation) => relation.name));
  }

  private isValidForgeIdentifier(identifier: string): boolean {
    return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(identifier);
  }

  private sanitizeForgeIdentifier(identifier: string, usedIdentifiers: Set<string>): string {
    let base = identifier.replace(/[^A-Za-z0-9_$]/g, '_');
    if (!base) {
      base = '_';
    }
    if (!/^[A-Za-z_$]/.test(base)) {
      base = `_${base}`;
    }
    let candidate = base;
    let suffix = 1;
    while (usedIdentifiers.has(candidate)) {
      candidate = `${base}_${suffix}`;
      suffix++;
    }
    usedIdentifiers.add(candidate);
    return candidate;
  }

  /**
   * Get type information for a specific atom
   * 
   * @param atomId - ID of the atom
   * @returns Type information implementing IType interface
   */
  public getAtomType(atomId: string): IType {
    const alloyType = getAtomType(this.alloyInstance, atomId);
    
    // Convert AlloyType to IType
    return {
      id: alloyType.id,
      types: alloyType.types,
      atoms: alloyType.atoms.map((atom: AlloyAtom) => {
        const skolemNames = getSkolemNamesForAtom(this.alloyInstance, atom.id);
        return {
          id: atom.id,
          label: atom.id,
          type: atom.type,
          labels: skolemNames.length > 0 ? { skolems: skolemNames } : undefined
        };
      }),
      isBuiltin: isBuiltin(alloyType)
    };
  }

  /**
   * Get all types defined in this instance
   * 
   * @returns Array of all types implementing IType interface
   */
  public getTypes(): readonly IType[] {
    const alloyTypes = getInstanceTypes(this.alloyInstance);
    
    return alloyTypes.map((alloyType: AlloyType) => ({
      id: alloyType.id,
      types: alloyType.types,
      atoms: alloyType.atoms.map((atom: AlloyAtom) => {
        const skolemNames = getSkolemNamesForAtom(this.alloyInstance, atom.id);
        return {
          id: atom.id,
          type: atom.type,
          label: atom.id,
          labels: skolemNames.length > 0 ? { skolems: skolemNames } : undefined
        };
      }),
      isBuiltin: isBuiltin(alloyType)
    }));
  }

  /**
   * Get all atoms in this instance
   * 
   * @returns Array of all atoms implementing IAtom interface
   */
  public getAtoms(): readonly IAtom[] {
    const alloyAtoms = getInstanceAtoms(this.alloyInstance);
    
    return alloyAtoms.map((alloyAtom: AlloyAtom) => {
      const skolemNames = getSkolemNamesForAtom(this.alloyInstance, alloyAtom.id);
      return {
        id: alloyAtom.id,
        type: alloyAtom.type,
        label: alloyAtom.id,
        labels: skolemNames.length > 0 ? { skolems: skolemNames } : undefined
      };
    });
  }

  /**
   * Get all relations in this instance
   * 
   * @returns Array of all relations implementing IRelation interface
   */
  public getRelations(): readonly IRelation[] {
    const alloyRelations = getInstanceRelations(this.alloyInstance);
    
    return alloyRelations.map((alloyRelation: AlloyRelation) => ({
      id: alloyRelation.id,
      name: alloyRelation.name, 
      types: alloyRelation.types,
      tuples: alloyRelation.tuples.map((tuple: AlloyTuple) => ({
        atoms: tuple.atoms,
        types: tuple.types
      }))
    }));
  }


  /**
   * Generate graph representation of this instance
   * Uses the existing alloy-graph generateGraph function
   * 
   * @param hideDisconnected - Whether to hide disconnected nodes
   * @param hideDisconnectedBuiltIns - Whether to hide disconnected built-in nodes
   * @returns Graph representation using graphlib.Graph
   */
  public generateGraph(hideDisconnected: boolean, hideDisconnectedBuiltIns: boolean): Graph {
    return generateGraph(this.alloyInstance, hideDisconnected, hideDisconnectedBuiltIns);
  }

  /**
   * Get the underlying AlloyInstance for backward compatibility
   * 
   * @returns The wrapped AlloyInstance
   */
  public getAlloyInstance(): AlloyInstance {
    return this.alloyInstance;
  }


  /**
   * Reify the instance to a Forge INST.
   * 
   * @returns An inst string representation of the AlloyInstance
   */
  public reify(): string {
    let inst = "";

    const instName = "builtinstance"; // Generate a unique name readable name.


    const PREFIX = `inst ${instName} {`;

    const POSTFIX = "}";

    // First declare all the ATOMS (I think in order?)
    let instanceTypes = this.alloyInstance.types;
    const emittedAtomIdentifiers = new Set<string>();
    const atomIdentifierMap = new Map<string, string>();
    // Diagnostics: track what the serializer had to drop or rewrite so the lossy
    // export of an edited instance isn't silent (issue #480).
    const sanitizedAtomRenames: string[] = [];
    const droppedTypeIds: string[] = [];
    const droppedRelationNames = new Set<string>();
    const droppedTupleFields = new Set<string>();
    // Atom ids that actually get bound under a declared, non-builtin sig. A tuple
    // referencing an atom NOT in here (and not a builtin literal) would be a
    // dangling reference in the inst — e.g. an atom of a sig that was dropped above.
    const boundAtomIds = new Set<string>();
    const getAtomIdentifier = (atomId: string): string => {
      const existing = atomIdentifierMap.get(atomId);
      if (existing) return existing;
      const sanitized = this.sanitizeForgeIdentifier(atomId, emittedAtomIdentifiers);
      atomIdentifierMap.set(atomId, sanitized);
      if (sanitized !== atomId) {
        sanitizedAtomRenames.push(`${atomId} -> ${sanitized}`);
      }
      return sanitized;
    };

    // Create a dict where the key is the type id and the value is the atoms
    let typeAtoms : Record<string, string[]> = {};
    for (let typeId in instanceTypes) {
        let type = instanceTypes[typeId];
        // Builtins (Int/String/seq/Int) are implicit in every spec — binding them is
        // redundant and usually rejected. Skipped silently; that's expected.
        if (isBuiltin(type)) {
            continue;
        }
        // A sig the parsed spec never declared (e.g. fabricated by addAtom on an
        // unknown type) cannot be bound without a compile error, so drop it — but
        // report it below, since the user's edit is being discarded.
        if (!this.declaredTypeIds.has(typeId)) {
            if (type.atoms.length > 0) {
                droppedTypeIds.push(typeId);
            }
            continue;
        }
        let atoms = type.atoms;
        typeAtoms[typeId] = atoms.map(atom => {
            boundAtomIds.add(atom.id);
            return `\`${getAtomIdentifier(atom.id)}`;
        });
    }

    // Then declare all the relations
    let instanceRelations = this.alloyInstance.relations;
    let relationDecls : Record<string, string[]> = {};

    for (let relationId in instanceRelations) {
        let relation = instanceRelations[relationId];
        // A field/relation the parsed spec never declared, or whose name isn't a
        // valid Forge identifier, can't be bound — drop it, but report it if it
        // carried tuples that are now being discarded.
        if (!this.declaredRelationNames.has(relation.name) || !this.isValidForgeIdentifier(relation.name)) {
            if (relation.tuples.length > 0) {
                droppedRelationNames.add(relation.name);
            }
            continue;
        }
        // A declared field can still carry a tuple that points at an atom which was
        // NOT bound — e.g. the user wired a declared relation to an atom of a sig
        // that got dropped above. Emitting it would dangle (`...->`Frog_9` with no
        // `Frog = ...`), so drop the offending tuple rather than the whole field.
        let tuples = relation.tuples.filter(tuple => {
            const allBound = tuple.atoms.every((a, i) => {
                const atomType = instanceTypes[tuple.types[i]];
                return (atomType && isBuiltin(atomType)) || boundAtomIds.has(a);
            });
            if (!allBound) {
                droppedTupleFields.add(relation.name);
            }
            return allBound;
        });
        let tupleStrings = tuples.map(tuple => {
            let tupleString = tuple.atoms.map((a, i) => {
                const atomType = instanceTypes[tuple.types[i]];
                return atomType && isBuiltin(atomType) ? a : `\`${getAtomIdentifier(a)}`;
            }).join("->");
            return `(${tupleString})`;
        });

        let relName = relation.name;

        // Relation NAMES are not unique — only the qualified id (`Sig<:label`) is.
        // Two relations sharing a name (e.g. the `Next` fields from two
        // `util/ordering` instantiations) must not clobber each other here, or one
        // is silently dropped from the INST. Accumulate (union) their tuples under
        // the shared name. NOTE: bare names may still be ambiguous for Forge when a
        // field is shared across opened modules — emitting qualified field
        // references is the deeper fix tracked in
        // https://github.com/sidprasad/spytial-core/issues/470
        relationDecls[relName] = (relationDecls[relName] ?? []).concat(tupleStrings);
    }

    // Now we can create the inst string
    // First, declare all the atoms in order
    for (let typeId in typeAtoms) {
        let atoms = typeAtoms[typeId];
        if(atoms.length > 0) {
            inst += `${typeId} = ${atoms.join("+")}\n`;
        }
    }

    // Then declare all the relations
    for (let relationId in relationDecls) {
        let tuples = relationDecls[relationId];
        if (tuples.length > 0) {
            inst += `${relationId} = ${tuples.join("+")}\n`;
        }
        else {
            inst += `no ${relationId}\n`;
        }
    }

    // Surface anything the export had to discard or rewrite, so a lossy round-trip
    // of an edited instance is visible rather than silent (issue #480).
    if (droppedTypeIds.length > 0) {
        console.warn(
            `AlloyDataInstance.reify: dropped ${droppedTypeIds.length} sig(s) not declared in the parsed spec ` +
            `(${droppedTypeIds.join(', ')}); their atoms were omitted from the inst.`
        );
    }
    if (droppedRelationNames.size > 0) {
        console.warn(
            `AlloyDataInstance.reify: dropped ${droppedRelationNames.size} field(s) not declared in the parsed spec ` +
            `or not a valid Forge identifier (${[...droppedRelationNames].join(', ')}); their tuples were omitted from the inst.`
        );
    }
    if (droppedTupleFields.size > 0) {
        console.warn(
            `AlloyDataInstance.reify: dropped tuple(s) from declared field(s) ` +
            `(${[...droppedTupleFields].join(', ')}) that referenced unbound atoms ` +
            `(atoms of a dropped/undeclared sig); they were omitted to avoid a dangling reference.`
        );
    }
    if (sanitizedAtomRenames.length > 0) {
        console.warn(
            `AlloyDataInstance.reify: rewrote ${sanitizedAtomRenames.length} atom id(s) to valid Forge identifiers ` +
            `(${sanitizedAtomRenames.join(', ')}).`
        );
    }

    return `${PREFIX}\n${inst}\n${POSTFIX}`;
  }


  /**
   * Remove an atom by ID
   * 
   * @param id - ID of the atom to remove
   */
  public removeAtom(id: string): void {
    
    // We actually have to 
    this.alloyInstance = removeInstanceAtom(this.alloyInstance, id);
    
    // Emit event
    this.emitEvent({
      type: 'atomRemoved',
      data: { atomId: id }
    });
  }

  public addAtom(atom: IAtom): void {
    // Convert IAtom to AlloyAtom
    const alloyAtom: AlloyAtom = {
      _: 'atom',
      id: atom.id,
      type: atom.type,
      
    };
    this.alloyInstance = addInstanceAtom(this.alloyInstance, alloyAtom);
    
    // Emit event
    this.emitEvent({
      type: 'atomAdded',
      data: { atom }
    });
  }

  public addRelationTuple(relationId: string, tuple: ITuple): void {
    // A field's types are positional, and reify() reads `tuple.types[i]` back
    // positionally to decide whether an atom is a builtin literal or a backticked
    // atom id — so settle the tuple against the field before writing it.
    const atoms = this.getAtoms();
    const relation = this.getRelations().find(r => r.id === relationId || r.name === relationId);
    const settled = settleTupleTypes(
      tuple,
      relation,
      atomId => atoms.find(a => a.id === atomId)?.type
    );

    // Convert ITuple to AlloyTuple
    const alloyTuple: AlloyTuple = {
      _: 'tuple',
      atoms: settled.tuple.atoms,
      types: settled.tuple.types
    };
    this.alloyInstance = addInstanceRelationTuple(this.alloyInstance, relationId, alloyTuple);

    // Emit event
    this.emitEvent({
      type: 'relationTupleAdded',
      data: { relationId, tuple: settled.tuple }
    });
  }

  public removeRelationTuple(relationId: string, t: ITuple): void {
    
    // Convert ITuple to AlloyTuple
    const alloyTuple: AlloyTuple = {
      _: 'tuple',
      atoms: t.atoms,
      types: t.types
    };


    this.alloyInstance = removeInstanceRelationTuple(this.alloyInstance, relationId, alloyTuple);
    
    // Emit event
    this.emitEvent({
      type: 'relationTupleRemoved',
      data: { relationId, tuple: t }
    });
  }

  /**
   * Adds data from another AlloyDataInstance to this instance.
   * 
   * @param dataInstance - The data instance to add from.
   * @param unifyBuiltIns - Whether to unify built-in types (reuse existing ones).
   * @returns True if the operation is successful, false otherwise.
   */
  public addFromDataInstance(dataInstance: IDataInstance, unifyBuiltIns: boolean): boolean {
    // Ensure the input is an AlloyDataInstance
    if (!isAlloyDataInstance(dataInstance)) {
      return false;
    }

    const alloyInstance = dataInstance.getAlloyInstance();
    const reIdMap = new Map<string, string>();

    // Add atoms
    getInstanceAtoms(alloyInstance).forEach(atom => {
      const isBuiltin = this.getAtomType(atom.id).isBuiltin;

      if (unifyBuiltIns && isBuiltin) {
        // Check if the built-in atom already exists
        const existingAtom = this.getAtoms().find(
          existing => existing.type === atom.type && existing.label === atom.id
        );

        if (existingAtom) {
          // Map the original atom ID to the existing atom ID
          reIdMap.set(atom.id, existingAtom.id);
          return; // Skip adding this atom
        }
      }

      // Generate a new ID for the atom to avoid conflicts
      const newId = `atom_${this.getAtoms().length + 1}`;
      reIdMap.set(atom.id, newId);

      // Use the addAtom method to add the atom
      this.addAtom({
        id: newId,
        type: atom.type,
        label: atom.id, // Use the original ID as the label
      });
    });

    // Add relations
    getInstanceRelations(alloyInstance).forEach(relation => {
      relation.tuples.forEach(tuple => {
        const mappedTuple: ITuple = {
          atoms: tuple.atoms.map(atomId => reIdMap.get(atomId) || atomId),
          types: tuple.types,
        };

        // Use the addRelationTuple method to add the tuple
        this.addRelationTuple(relation.id, mappedTuple);
      });
    });

    // Add types
    getInstanceTypes(alloyInstance).forEach(type => {
      const existingType = this.getTypes().find(t => t.id === type.id);
      if (!existingType) {
        // Add the type if it doesn't exist.
        // I *think* built-in types already exist in the instance.
        this.alloyInstance.types[type.id] = {
          _: 'type',
          id: type.id,
          types: type.types,
          atoms: type.atoms.map(atom => ({
            _: 'atom',
            id: reIdMap.get(atom.id) || atom.id,
            type: atom.type,
          })),
          meta: {
            builtin: false,
            abstract: false,
            enum: false,
            one: false,
            private: false,
          },
        };
      } else {
        // If the type already exists, i think we are good.
      }
    });

    return true;
  }
}

/**
 * Factory function to create AlloyDataInstance from AlloyInstance
 * 
 * @param alloyInstance - AlloyInstance to wrap
 * @returns IDataInstance implementation for Alloy
 */
export function createAlloyDataInstance(alloyInstance: AlloyInstance): IDataInstance {
  return new AlloyDataInstance(alloyInstance);
}

/**
 * Type guard to check if an IDataInstance is an AlloyDataInstance
 * 
 * @param instance - IDataInstance to check
 * @returns True if the instance is an AlloyDataInstance
 */
export function isAlloyDataInstance(instance: IDataInstance): instance is AlloyDataInstance {
  return instance instanceof AlloyDataInstance;
}


/**
 * Creates an empty AlloyDataInstance with default Alloy built-in types
 * Includes fundamental types like univ, Int, seq/Int that are always present in Alloy
 * 
 * @returns A new AlloyDataInstance with built-in types but no user-defined atoms or relations
 */
export function createEmptyAlloyDataInstance(): AlloyDataInstance {
  // Default built-in types that should always be present in Alloy instances
  const defaultTypes: Record<string, AlloyType> = {
    'univ': {
      _: 'type',
      id: 'univ',
      types: [],
      atoms: [],
      meta: {
        builtin: true,
        abstract: false,
        enum: false,
        one: false,
        private: false
      }
    },
    'Int': {
      _: 'type', 
      id: 'Int',
      types: ['Int', 'univ'],
      atoms: [  ],
      meta: {
        builtin: true,
        abstract: false,
        enum: false,
        one: false,
        private: false
      }
    },
    'seq/Int': {
      _: 'type',
      id: 'seq/Int', 
      types: ['seq/Int', 'univ'],
      atoms: [],
      meta: {
        builtin: true,
        abstract: false,
        enum: false,
        one: false,
        private: false
      }
    }
  };


  const emptyAlloyInstance: AlloyInstance = {
    types: defaultTypes,
    relations: {},
    skolems: {}
  };

  return new AlloyDataInstance(emptyAlloyInstance);
}